import { Component, Input, Output, EventEmitter, OnInit, OnChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';

import {
  FrequencyInfo,
  DayInfo,
  ClientFrequencyData,
  ClientFrequencyDisplayInputs,
  CalendarModalData,
  CustomFrequenciesModalData,
  FrequencyUpdateEvent
} from './client-frequency-display.interface';

import { CalendarPeriodicityComponent } from '../../sub-route-periodicity/calendar-periodicity/calendar-periodicity.component';
import { CustomDatesModalComponent } from '../../sub-route-periodicity/modal-w-frequencies/modal-w-frequencies.component';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
  selector: 'app-client-frequency-display',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule
  ],
  templateUrl: './client-frequency-display.component.html',
  styleUrls: ['./client-frequency-display.component.scss']
})
export class ClientFrequencyDisplayComponent implements OnInit, OnChanges {
  @Input() subRouteId!: string;
  @Input() subRouteNormalId!: number;
  @Input() businessPartnerId!: string;
  @Input() businessPartnerNormalId!: number;
  @Input() frequenciesFromThisSubRoute: FrequencyInfo[] = [];

  // Output events for parent component to handle data refresh
  @Output() frequencyUpdated = new EventEmitter<FrequencyUpdateEvent>();
  @Output() refreshRequested = new EventEmitter<void>();

  // Processed frequency data
  clientFrequencyData: ClientFrequencyData = {
    bpDayFrecuenciesLength: 0,
    bpCustomFrecuenciesLength: 0,
    bpDaysInAllFrecuencies: ''
  };

  // Days mapping (replica from original component)
  private readonly daysList: DayInfo[] = [
    { code: 1, day: "LU" },
    { code: 2, day: "MA" },
    { code: 3, day: "MI" },
    { code: 4, day: "JU" },
    { code: 5, day: "VI" },
    { code: 6, day: "SA" },
    { code: 7, day: "DO" }
  ];

  constructor(
    private dialog: MatDialog,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.processFrequencyData();
  }

  ngOnChanges(): void {
    this.processFrequencyData();
  }

  private processFrequencyData(): void {
    if (!this.frequenciesFromThisSubRoute || this.frequenciesFromThisSubRoute.length === 0) {
      this.clientFrequencyData = {
        bpDayFrecuenciesLength: 0,
        bpCustomFrecuenciesLength: 0,
        bpDaysInAllFrecuencies: '-'
      };
      return;
    }

    // Process frequency counts by type (replica from original logic)
    this.clientFrequencyData.bpDayFrecuenciesLength = this.frequenciesFromThisSubRoute
      .filter((f: FrequencyInfo) => f.type === 'W').length;
    
    this.clientFrequencyData.bpCustomFrecuenciesLength = this.frequenciesFromThisSubRoute
      .filter((f: FrequencyInfo) => f.type === 'C').length;

    // Extract unique days and map to day names (replica from original logic)
    const fuseDays: number[] = [...new Set(
      this.frequenciesFromThisSubRoute.flatMap((f: FrequencyInfo) => f.days)
    )].sort((a: number, b: number) => a - b);

    this.clientFrequencyData.bpDaysInAllFrecuencies = fuseDays
      .map((num: number) => this.daysList.find((day: DayInfo) => day.code === num)?.day || '')
      .filter((day: string) => day)
      .join(', ') || '-';
  }

  // Type guard for frequency validation
  private isValidFrequency(frequency: unknown): frequency is FrequencyInfo {
    return typeof frequency === 'object' && 
           frequency !== null && 
           '_id' in frequency && 
           'type' in frequency && 
           'days' in frequency;
  }

  // Modal methods (replica from original component)
  openModalCalendar(): void {
    const dataToSend: CalendarModalData = {
      subRouteId: this.subRouteId,
      businessPartners: [{
        _id: this.businessPartnerId,
        id: this.businessPartnerNormalId
      }],
      frecuencySelected: null
    };

    const dialogRef = this.dialog.open(CalendarPeriodicityComponent, {
      width: '95%',
      height: '95%',
      maxHeight: '95vh',
      maxWidth: '1400px',
      data: dataToSend,
    });

    dialogRef.afterClosed().subscribe((result: unknown) => {
      if (result) {
        // Emit events to parent component to refresh data
        this.emitFrequencyUpdateEvent('updated');
        this.refreshRequested.emit();
      }
    });
  }

  openCustomFrequenciesModal(): void {
    if (this.clientFrequencyData.bpCustomFrecuenciesLength === 0) {
      return;
    }

    const modalData: CustomFrequenciesModalData = {
      frequencies: {
        _id: this.businessPartnerId,
        id: this.businessPartnerNormalId,
        frequenciesFromThisSubRoute: this.frequenciesFromThisSubRoute
      },
      subRoute: {
        _id: this.subRouteId,
        id: this.subRouteNormalId
      }
    };

    const dialogRef = this.dialog.open(CustomDatesModalComponent, {
      width: '95%',
      height: '95%',
      maxWidth: '1400px',
      maxHeight: '95vh',
      data: modalData
    });

    dialogRef.afterClosed().subscribe((result: unknown) => {
      if (result) {
        // Emit events to parent component to refresh data
        this.emitFrequencyUpdateEvent('updated');
        this.refreshRequested.emit();
      }
    });
  }

  private emitFrequencyUpdateEvent(action: 'updated' | 'deleted' | 'created'): void {
    const updateEvent: FrequencyUpdateEvent = {
      subRouteId: this.subRouteId,
      businessPartnerId: this.businessPartnerId,
      action: action,
      timestamp: new Date()
    };
    
    this.frequencyUpdated.emit(updateEvent);
  }

  // Getter methods for template
  get displayDays(): string {
    return this.clientFrequencyData.bpDaysInAllFrecuencies;
  }

  get dayFrequenciesCount(): number {
    return this.clientFrequencyData.bpDayFrecuenciesLength;
  }

  get customFrequenciesCount(): number {
    return this.clientFrequencyData.bpCustomFrecuenciesLength;
  }

  get hasCustomFrequencies(): boolean {
    return this.clientFrequencyData.bpCustomFrecuenciesLength > 0;
  }

  // Tooltip methods with symbology
  getDaysTooltip(): string {
    const days = this.displayDays;
    if (days === '-' || !days) {
      return '❌ Sin días asignados - No hay frecuencias configuradas';
    }
    
    const dayNames = this.getDayNamesFromAbbreviation(days);
    return `📅 Días de visita programados:\n${dayNames}\n\n💡 LU=Lunes, MA=Martes, MI=Miércoles, JU=Jueves, VI=Viernes, SA=Sábado, DO=Domingo`;
  }

  getCustomFrequenciesTooltip(): string {
    if (!this.hasCustomFrequencies) {
      return '⚪ Frecuencias personalizadas (Custom)\n❌ No hay frecuencias personalizadas configuradas';
    }
    
    return `🎯 Frecuencias personalizadas (Custom)\n📊 ${this.customFrequenciesCount} frecuencia${this.customFrequenciesCount > 1 ? 's' : ''} personalizada${this.customFrequenciesCount > 1 ? 's' : ''}\n\n🖱️ Click para ver y gestionar fechas específicas`;
  }

  private getDayNamesFromAbbreviation(abbreviatedDays: string): string {
    const dayMap: Record<string, string> = {
      'LU': 'Lunes',
      'MA': 'Martes', 
      'MI': 'Miércoles',
      'JU': 'Jueves',
      'VI': 'Viernes',
      'SA': 'Sábado',
      'DO': 'Domingo'
    };

    return abbreviatedDays
      .split(', ')
      .map(abbr => dayMap[abbr.trim()] || abbr.trim())
      .join(', ');
  }
}